import * as React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {AddRowDivider} from './add-row-divider';
import {DroppableRowDivider} from './droppable-row-divider';
import type {useDragIndicator} from '../hooks/use-drag-indicator';
import {DRAG_TYPE, type DragType} from './dnd-config';


export type InterRowDividerStackProps = {
  /** Position relative to the dashboard's `rowOrder`. -1 prepends. */
  afterIndex: number;
  pathname: string;
  activeDragType: DragType | null;
  indicatorTarget: ReturnType<typeof useDragIndicator>['indicatorTarget'];
  onAddSection: (afterIndex: number) => void;
};

/**
 * One inter-row gap on the canvas. Renders one of three things:
 *   1. No drag in progress — `<AddRowDivider>` with the "+ Section" /
 *      "+ Report" buttons.
 *   2. Drag in progress, KPI tile being dragged — a KPI new-row drop band.
 *   3. Drag in progress, viz tile being dragged — a viz new-row drop band.
 *
 * Only ONE divider renders at a time per gap (the one matching the active
 * drag type, or the add-row buttons when idle). The previous version of
 * this code rendered both KPI and viz dividers in parallel and disabled
 * the wrong one — that read as visual noise to users.
 *
 * Used twice in the canvas (once between every pair of rows, once after
 * the last row), which is why this is its own component instead of being
 * inlined.
 */
export function InterRowDividerStack({
  afterIndex,
  pathname,
  activeDragType,
  indicatorTarget,
  onAddSection,
}: InterRowDividerStackProps) {
  const {t} = useLabelTranslation();

  if (activeDragType === null) {
    return (
      <AddRowDivider
        afterIndex={afterIndex}
        pathname={pathname}
        onAddSection={onAddSection}
      />
    );
  }

  if (activeDragType === DRAG_TYPE.KPI) {
    return (
      <DroppableRowDivider
        afterIndex={afterIndex}
        variant="kpi"
        indicatorTarget={indicatorTarget}
        activeDragType={activeDragType}
        label={t('New_KPI_row', 'New KPI row')}
      />
    );
  }

  return (
    <DroppableRowDivider
      afterIndex={afterIndex}
      variant="viz"
      indicatorTarget={indicatorTarget}
      activeDragType={activeDragType}
      label={t('New_chart_or_table_row', 'New chart or table row')}
    />
  );
}
